import { FilterAPI } from '@apis/filter';
import { SearchAPI } from '@apis/search';
import { registryComponent } from '@boost-sd/components-registry/registry';
import { useUpdateEffect } from '@boost-sd/core-js';
import {
  getAppHistory,
  getQueryParamByKey,
  getQueryParamsState,
  removeQueryParam,
  setQueryParam,
  setQueryParams,
  useListenHistoryEvent,
} from '@boost-sd/core-js/history';
import { NEXT_PAGE, PREV_PAGE } from '@constants/pagination';
import { SCOPED_COLLECTION, scopedFilterParams } from '@constants/scopedSuggestion';
import useGeneralSettings from '@hooks/useGeneralSettings';
import useMountEffect from '@hooks/useMountEffect';
import useStateAsExternalStore from '@hooks/useStateAsExternalStore';
import { useValidateBreakpoint } from '@providers/AppProvider/Breakpoint/hooks';
import type { ProductItem } from '@providers/ProductProvider';
import { useAdditionalElementThemeSettings } from '@providers/ThemeProvider/Provider/AdditionalElementThemeSettings';
import { useProductListThemeSettings } from '@providers/ThemeProvider/Provider/ProductListThemeSettings';
import type { Dict } from '@types';
import {
  getSessionStorage,
  getTermValueFromUrl,
  isBadUrl,
  isCollectionPage,
  isSearchPage,
  removeDefaultOptionsProducts,
  resetPaginationSessionStorage,
  slugify,
  stripHtml,
  toItemArray,
} from '@utils';
import { cloneDeep } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { clsNameMap as clsNameMapInCollectionSearch } from '@/widgets/InCollectionSearch';

import type {
  CurrentCollectionFilterState,
  FilterData,
  FilterOptionType,
  FilterParams,
  FilterStateValue,
  LoadProductsBehavior,
  ScopedFilter,
  SharedAdditionalElementFilterData,
} from '../types';
import {
  checkAndRemoveScopedParam,
  checkCollectionPageWhenRemoveParams,
  deduplicateFilterFromScopedParam,
  getConditionFromBEReturn,
  getCurrentCollectionId,
  getCurrentCollectionSelected,
  getDefaultLimit,
  getExtraParamsByFilterDisplayType,
  getFilterPageByDefault,
  getFirstFilterParamsAndScopedType,
  getOtherParams,
  getParamMap,
  getSortBy,
  handleOnPopHistory,
  handleOnReplaceHistory,
  longToShortFilterParams,
  removeFieldNotShowInURL,
} from '../utils';
import { useFilterSettings } from './FilterSettings';

const FilterStateContext = createContext<FilterStateValue | null>(null);

type FilterStateProviderProps = {
  showLimitOnUrl?: boolean;
};

const FilterStateProviderComponent = ({
  children,
  showLimitOnUrl = false,
}: PropsWithChildren<FilterStateProviderProps>) => {
  const mobileBreakpoint = useValidateBreakpoint('mobile');

  const {
    generalSettings: {
      collection_id,
      collection_handle,
      current_tags,
      collection_tags,
      customizeSearchParams,
      termKey,
    },
  } = useGeneralSettings();

  const currentCollectionSearchParam = getTermValueFromUrl(termKey);
  const {
    filterEverywhereCollectionId,
    availableAfterFiltering,
    showOutOfStockOption,
    productAndVariantAvailable,
    showVariantsAsProduct,
    priceMode,
    tagMode,
    sortingAvailableFirst,
    vendorParam,
    typeParam,
    filterPrefixParam,
    urlScheme,
    isShortenUrlParam,
    shortenUrlParamList,
    filterLayout,
    requestInstantly,
    ignoreCharacterURL,
  } = useFilterSettings();

  const {
    pagination: { paginationType },
  } = useAdditionalElementThemeSettings();

  const [historyCollection, setHistoryCollection] = useState<Dict>({
    [slugify(collection_tags?.[0] || collection_handle || (Math.random() + 1).toString(36))]: {
      collectionId: collection_id,
      handle: collection_handle,
      tag: collection_tags?.[0] || undefined,
    },
  });

  const [scopedFilterType, setScopedFilterType] = useState<ScopedFilter | null>(null);

  const [{ longParamMap, shortParamsMap }] = useState(() => {
    return getParamMap(isShortenUrlParam, urlScheme, shortenUrlParamList);
  });

  const [currentCollectionSelected, setCurrentCollectionSelected] =
    useState<CurrentCollectionFilterState>(() => {
      if (isSearchPage()) {
        const collectionId =
          getQueryParamByKey(`${filterPrefixParam}c_collection`) ||
          getQueryParamByKey(`collection`) ||
          getQueryParamByKey('collections') ||
          getQueryParamByKey(SCOPED_COLLECTION);

        return {
          collectionId: collectionId ? collectionId : 0,
          handle: undefined,
          tag: getQueryParamByKey(`${filterPrefixParam}ct_collection`),
        };
      }

      return {
        collectionId: collection_handle === 'all' ? 0 : collection_id.toString(),
        handle: collection_handle,
        tag: current_tags?.[0],
      };
    });

  const [filterOptionSelected, setFilterOptionSelected] = useState<FilterOptionType | null>(null);

  const isShortenURL = isShortenUrlParam && urlScheme !== 0 && shortenUrlParamList.length > 0;

  const getFilterParams = () => {
    // check badUrl when reload
    const _isBadUrl = isBadUrl(window.location.href, ignoreCharacterURL);
    if (_isBadUrl) {
      window.location.href = window.location.pathname;
    }

    const { filterParams, scopedType } = getFirstFilterParamsAndScopedType(
      filterPrefixParam,
      scopedFilterType,
      isShortenURL,
      longParamMap,
      collection_tags
    );

    // if exist scopedType -> setScopedType
    if (scopedType) {
      setScopedFilterType(scopedType as ScopedFilter);
    }

    return filterParams;
  };

  const currentCollectionId = getCurrentCollectionId(collection_handle, collection_id);

  const firstFilterParams = getFilterParams();

  const [filterParams, setFilterParams] = useState<FilterParams>(firstFilterParams);

  const [ajaxLoading, setAjaxLoading] = useState(false);

  const { productsPerPage } = useProductListThemeSettings();

  const defaultLimit = productsPerPage as number;

  const [limit, setLimit] = useState<number>(getDefaultLimit(showLimitOnUrl, defaultLimit));
  const [page, setPage] = useState<number>(getFilterPageByDefault());
  const [sortBy, setSortBy] = useState<string>(getSortBy(currentCollectionId));

  const [filterData, setFilterData] = useState<FilterData>({
    loading: true,
    data: null,
  });

  const [sharedAdditionalElementData, setSharedAdditionalElementData] =
    useState<SharedAdditionalElementFilterData>({
      loadingAdditional: true,
      fromProductIndex: 0,
      toProductIndex: 0,
      page: 1,
      limit,
      sortBy,
      loadProductsBehavior: 'refresh',
      totalPages: 0,
      totalProducts: 0,
      currentProductCount: 0,
      searchPanelTotalCollections: 0,
      searchPanelTotalPages: 0,
    });

  /**
   * callback function setCurrentCollectionSelected
   * */
  useUpdateEffect(() => {
    const history = getAppHistory();
    const { collectionId, handle, tag, fromBack } = currentCollectionSelected;

    // setCurrentCollectionSelected from back button, no call api
    if (fromBack) {
      return;
    }

    const _filterParams: Dict = {};

    // set null for collection to close option
    if (filterLayout === 'horizontal') setFilterOptionSelected(null);

    const scopedCollection = getQueryParamByKey(SCOPED_COLLECTION);
    if (scopedCollection && scopedCollection !== collectionId?.toString()) {
      removeQueryParam(SCOPED_COLLECTION);
    }

    if (isSearchPage()) {
      const _collectionId = +collectionId === 0 ? null : collectionId?.toString();

      handleChangeFilterParams(
        {
          ..._filterParams,
          [`${filterPrefixParam}c_collection`]: _collectionId,
          [`${filterPrefixParam}ct_collection`]: tag,
          collection_scope: _collectionId,
        },
        {
          requestInstantlyHorizontal: true,
          collectionFromSearchPage: true,
        }
      );
    } else {
      // only apply for collection page
      //Fixme: Cover case collection multi level has multi tag
      _filterParams.tag = tag;

      // collection page
      // collection select or unSelected
      handleChangeFilterParams(_filterParams, {
        requestInstantlyHorizontal: true,
        ignoreSetQueryParams: true,
      });

      let collectionUrlPath = `/collections/${handle}${tag ? `/${slugify(tag)}` : ''}`;

      // in-collection search in collection page
      if (currentCollectionSearchParam) {
        collectionUrlPath += `?q=${currentCollectionSearchParam}`;
      }
      history.push(collectionUrlPath);
    }
  }, [currentCollectionSelected]);

  /**
   * callback function setLimit
   * */
  useUpdateEffect(() => {
    if (showLimitOnUrl) {
      setQueryParam('limit', String(limit), true);
    }

    getFilterData(
      {
        page: 1,
        filterParams,
        inCollectionSearchValue: currentCollectionSearchParam,
        limit,
      },
      { loadProductsBehavior: 'refresh' }
    );
  }, [limit]);

  /**
   * callback function setPage
   * */
  useUpdateEffect(() => {
    setQueryParam('page', String(page), true);
    getFilterData(
      {
        page,
        filterParams,
        inCollectionSearchValue: currentCollectionSearchParam,
        limit,
      },
      { loadProductsBehavior: 'refresh' }
    );
  }, [page]);

  /**
   * callback preview change default limit
   * */
  useUpdateEffect(() => {
    setLimit(defaultLimit);

    resetPaginationSessionStorage();
  }, [defaultLimit]);

  /**
   * callback function setSortBy
   * */
  useUpdateEffect(() => {
    getFilterData(
      {
        page,
        filterParams,
        inCollectionSearchValue: currentCollectionSearchParam,
        limit,
      },
      { loadProductsBehavior: 'refresh' }
    );
  }, [sortBy]);

  useEffect(() => {
    window.addEventListener('boost-sd-remove-filter-params', (e) => {
      const { optionId, value } = e.detail;

      removeFilterParams(optionId, value);
    });
  }, []);

  const getFilterData = async (
    payload: {
      filterParams?: FilterParams;
      page?: number;
      inCollectionSearchValue?: string;
      limit: number;
    },
    options: {
      loadProductsBehavior?: LoadProductsBehavior;
    } = { loadProductsBehavior: 'refresh' },
    collectionSelected?: CurrentCollectionFilterState
  ) => {
    const extraParamsByFilterDisplayType = Object.keys(payload.filterParams || filterParams).reduce(
      (params, optionId) => {
        const filterOptionData: FilterOptionType | undefined =
          filterData.data?.filter?.options?.find(
            ({ filterOptionId }) => filterOptionId === optionId
          );

        if (!filterOptionData) return params;

        return {
          ...params,
          ...getExtraParamsByFilterDisplayType(filterOptionData),
        };
      },
      {}
    );

    const page = payload.page || getFilterPageByDefault();
    const limit = payload.limit;

    const andConditionFilterOption = getQueryParamsState({
      filter: (key) => key.endsWith('_and_condition') || key.endsWith('_exclude_from_value'),
    });
    const finalFilterParams: Dict = {
      ...payload.filterParams,
      ...extraParamsByFilterDisplayType,
      ...getOtherParams(
        payload.filterParams,
        filterEverywhereCollectionId,
        currentCollectionSearchParam,
        urlScheme,
        collectionSelected,
        currentCollectionSelected,
        {
          availableAfterFiltering,
          productAndVariantAvailable,
          showOutOfStockOption,
          showVariantsAsProduct,
          priceMode,
          tagMode,
          sortingAvailableFirst,
          vendorParam,
          typeParam,
        },
        filterPrefixParam
      ),
      ...andConditionFilterOption,
      ...getConditionFromBEReturn(filterParams, filterData),
    };

    // add params for in collection search
    if (payload.inCollectionSearchValue) {
      finalFilterParams[termKey] = payload.inCollectionSearchValue;

      if (isCollectionPage()) {
        finalFilterParams['incollection_search'] = true;
        finalFilterParams['event_type'] = 'incollection_search';
      }
    }

    if (!filterData.loading) {
      setFilterData((prevState) => ({
        ...prevState,
        loading: true,
      }));
    }

    const getDataAction =
      payload.inCollectionSearchValue || isSearchPage()
        ? SearchAPI.searchInCollection
        : FilterAPI.get;

    return getDataAction(finalFilterParams, {
      page,
      limit,
      sort: getSortBy(currentCollectionId),
      customizeSearchParams,
    }).then((res: any) => {
      const { loadProductsBehavior } = options || {};
      const currentPageParams = getFilterPageByDefault();

      if (currentPageParams !== page) {
        /**
         * Ticket: PFSN-48155
         * Target: Only push history when pagination type is page link, otherwise replace to avoid duplicated history stack
         */
        setQueryParam('page', String(page), paginationType !== 'default');
      }

      if (Math.ceil(res.total_product / limit) > 0 && Math.ceil(res.total_product / limit) < page) {
        /**
         * Target: check and reset if page params invalid
         */
        setPage(1);
        return;
      }

      setFilterData((prevState) => {
        let products = res.products;
        const prevProducts = prevState.data?.products;

        if (loadProductsBehavior === 'more' && prevProducts) {
          products = [...prevProducts, ...products];
        }

        if (loadProductsBehavior === 'previous' && prevProducts) {
          products.push(...prevProducts);
        }

        /** REMOVE DEFAULT OPTION */
        products = removeDefaultOptionsProducts(products);

        setSharedAdditionalElementData((prevState) => {
          let searchPanelState = {};
          if (payload.inCollectionSearchValue) {
            searchPanelState = {
              searchPanelTotalCollections: res.total_collection,
              searchPanelTotalPages: res.total_page,
            };
          }

          return {
            ...prevState,
            loadingAdditional: false,
            page: products.length === 0 ? 1 : page,
            limit,
            loadProductsBehavior,
            totalPages: Math.ceil(res.total_product / limit),
            totalProducts: res.total_product,
            currentProductCount: products.length,
            ...searchPanelState,
          };
        });

        return {
          data: {
            ...res,
            products,
          },
          limit,
          loading: false,
        };
      });

      return res;
    });
  };

  const handleChangeFilterParams = useCallback(
    (
      newFilterParams: FilterParams,
      option?: {
        preventSetQueryParams?: boolean;
        requestInstantlyHorizontal?: boolean;
        fromBack?: boolean;
        ignoreSetQueryParams?: boolean;
        collectionFromSearchPage?: boolean;
      }
    ) => {
      if (!option?.fromBack) {
        resetPaginationSessionStorage();
      }

      // hard diff copy, prevent object reference
      let _newParamsFormatURL = cloneDeep(newFilterParams); //JSON.parse(JSON.stringify(newFilterParams));

      // assign optionId undefine -> remove key in URL because dif copy remove value undefine
      Object.keys(newFilterParams)?.forEach((optionId) => {
        if (!newFilterParams[optionId]) {
          _newParamsFormatURL[optionId] = undefined;
        }
      });

      // shorten URL handle
      if (isShortenURL) longToShortFilterParams(_newParamsFormatURL, urlScheme, shortParamsMap);

      // because convert delete key have value undefined -> should handle after longToShortFilterParams
      const isEmpty = Object.keys(_newParamsFormatURL).length === 0;
      if (isEmpty) {
        Object.keys(newFilterParams).forEach((key) => {
          if (newFilterParams[key] === undefined) {
            const _keyShort = shortParamsMap.get(key);

            _newParamsFormatURL[_keyShort] = undefined;
          }
        });
      }

      setFilterParams(newFilterParams);

      // apply for style horizontal and desktop and !requestInstantly
      if (
        !mobileBreakpoint &&
        filterLayout === 'horizontal' &&
        !requestInstantly &&
        !option?.requestInstantlyHorizontal
      )
        return;

      if (!option?.preventSetQueryParams) {
        _newParamsFormatURL =
          deduplicateFilterFromScopedParam(
            _newParamsFormatURL,
            scopedFilterParams,
            scopedFilterType
          ) || {};

        // delete some field has in filterParams but not show in URL
        const fieldsNotShowURL = ['collection_scope'];

        _newParamsFormatURL = removeFieldNotShowInURL(fieldsNotShowURL, _newParamsFormatURL);

        if (!option?.ignoreSetQueryParams) {
          if (option?.collectionFromSearchPage) {
            Object.assign(_newParamsFormatURL, {
              q: getQueryParamByKey('q'),
            });
          }

          setQueryParams(
            {
              ..._newParamsFormatURL,
              /**
               * @description Reset page param when change filter params
               */
              page: undefined,
            },
            {
              /**
               * @description Force change URL is shortenURL when change filter params
               */
              isShortenURL: isShortenURL,
              force: option?.collectionFromSearchPage,
            }
          );
        }
      }

      if (option?.fromBack && filterLayout === 'horizontal') {
        window.dispatchEvent(
          new CustomEvent('boost-sd-click-back-button-horizontal', {
            detail: {
              filterParamsHorizontal: newFilterParams,
            },
          })
        );
      }

      const latestQueryStringInCollectionSearch = getQueryParamByKey(termKey) as string;
      const inputInCollectionSearch = document.querySelector(
        `.${clsNameMapInCollectionSearch.elm('input')}`
      ) as HTMLInputElement;

      if (inputInCollectionSearch) {
        inputInCollectionSearch.value = latestQueryStringInCollectionSearch;
      }

      return getFilterData({
        page: option?.fromBack ? undefined : 1,
        filterParams: newFilterParams,
        inCollectionSearchValue: latestQueryStringInCollectionSearch,
        limit,
      });
    },
    [getFilterData]
  );

  const getQueryParamsFromURL = () => {
    const filterParams = getFilterParams();

    const currentCollectionSelected = getCurrentCollectionSelected(
      historyCollection,
      filterPrefixParam
    );

    filterParams.collection_scope = currentCollectionSelected.collectionId;
    filterParams.tag = currentCollectionSelected.tag;
    setCurrentCollectionSelected(currentCollectionSelected);

    setSortBy(getSortBy(currentCollectionId));

    return handleChangeFilterParams(filterParams, {
      preventSetQueryParams: true,
      requestInstantlyHorizontal: true,
      fromBack: true,
    });
  };

  useListenHistoryEvent({
    onPop: () => handleOnPopHistory(getQueryParamsFromURL),
    onReplace: () => handleOnReplaceHistory(getQueryParamsFromURL),
  });

  const addFilterParams = useCallback(
    (optionId: string, value: string, option?: { force?: boolean }) => {
      const currentValues = filterParams[optionId] || [];

      if (!currentValues.includes(value)) {
        const filterOptionData = filterData.data?.filter?.options?.find(
          ({ filterOptionId }) => filterOptionId === optionId
        );

        if (!filterOptionData) return;

        if (filterOptionData.selectType === 'single' || option?.force) {
          return handleChangeFilterParams({
            ...filterParams,
            [optionId]: [value],
          });
        }

        const newFilterOptionValues = [...currentValues, value];

        const newFilterParams = {
          ...filterParams,
          [optionId]: newFilterOptionValues,
        };

        return handleChangeFilterParams(newFilterParams);
      }
    },
    [filterParams, filterData.data?.filter?.options, handleChangeFilterParams]
  );

  const addInCollectionSearchParams = (value: string) => {
    let newFilterParams = cloneDeep(filterParams);

    const isShortenURL = isShortenUrlParam && urlScheme !== 0 && shortenUrlParamList.length > 0;
    if (isShortenURL) {
      // Long to short
      longToShortFilterParams(newFilterParams, urlScheme, shortParamsMap);
    }

    const fieldsNotShowURL = ['collection_scope'];
    newFilterParams = removeFieldNotShowInURL(fieldsNotShowURL, newFilterParams);
    setQueryParams({
      ...newFilterParams,
      [termKey]: stripHtml(value),
      /**
       * @description Reset page param when change search params
       */
      page: undefined,
    });

    resetPaginationSessionStorage();

    return getFilterData({
      filterParams: filterParams,
      inCollectionSearchValue: value,
      limit,
    });
  };

  const removeFilterParamsOption = (optionId: string, type?: 'all' | '') => {
    Object.entries(scopedFilterParams).forEach(([scopedType, scopedFilterParam]) => {
      if (type === 'all' || (scopedFilterParam === optionId && getQueryParamByKey(scopedType))) {
        checkAndRemoveScopedParam('', '', 'all');
      }
    });

    if (type === 'all') {
      let _newFilterParams = filterParams;

      const hasCollection =
        filterParams['collection'] ||
        filterParams['collections'] ||
        filterParams[`${filterPrefixParam}c_collection`] ||
        isCollectionPage();
      Object.keys(filterParams).forEach((key: string) => {
        _newFilterParams[key] = undefined;
      });

      const { newFilterParams, currentCollectionSelected } = checkCollectionPageWhenRemoveParams(
        hasCollection,
        _newFilterParams,
        historyCollection,
        currentCollectionId
      );

      _newFilterParams = newFilterParams;
      setCurrentCollectionSelected(currentCollectionSelected);

      return handleChangeFilterParams(_newFilterParams, {
        requestInstantlyHorizontal: filterLayout === 'horizontal' ? true : false,
      });
    } else {
      const _newFilterParams = filterParams;
      _newFilterParams[optionId] = undefined;

      // if clear collection option then reset collection when in search page as trigger select collection all
      if (optionId.endsWith('c_collection')) {
        return setCurrentCollectionSelected({
          handle: 'all',
          collectionId: collection_id || 0,
          tag: undefined,
        });
      }

      return handleChangeFilterParams(_newFilterParams, {
        requestInstantlyHorizontal: filterLayout === 'horizontal' ? true : false,
      });
    }
  };

  const removeFilterParams = useCallback(
    (optionId: string, value: string | number, option?: { forceInstantly?: boolean }) => {
      if (typeof value !== 'string') value = value.toString();

      checkAndRemoveScopedParam(optionId, value?.replace('&', 'AND'));

      const currentValues = toItemArray(filterParams[optionId] || []);
      const indexValue = currentValues.indexOf(value);
      if (indexValue !== -1 && Array.isArray(currentValues)) {
        const nextValues = [
          ...currentValues.slice(0, indexValue),
          ...currentValues.slice(indexValue + 1),
        ];

        let _newFilterParams = { ...filterParams };
        const hasCollection =
          _newFilterParams['collection'] ||
          _newFilterParams['collections'] ||
          _newFilterParams[`${filterPrefixParam}c_collection`] ||
          isCollectionPage();

        const { newFilterParams, currentCollectionSelected } = checkCollectionPageWhenRemoveParams(
          hasCollection,
          _newFilterParams,
          historyCollection,
          currentCollectionId
        );

        _newFilterParams = newFilterParams;
        setCurrentCollectionSelected(currentCollectionSelected);

        if (nextValues.length) {
          _newFilterParams[optionId] = [...nextValues];
        } else {
          _newFilterParams[optionId] = undefined;
        }

        return handleChangeFilterParams(_newFilterParams, {
          requestInstantlyHorizontal:
            filterLayout === 'horizontal' && option?.forceInstantly ? true : false,
        });
      }
    },
    [filterParams, handleChangeFilterParams]
  );

  const toggleFilterParams = useCallback(
    (optionId: string, value: string) => {
      if (scopedFilterType) {
        const scopedParam = scopedFilterParams[scopedFilterType];
        if (filterParams[scopedParam]?.includes(value)) {
          optionId = scopedParam;
        }
      }

      const currentValues = filterParams[optionId] || [];
      const indexValue = currentValues.indexOf(value);
      if (indexValue !== -1) {
        return removeFilterParams(optionId, value);
      } else {
        addFilterParams(optionId, value);
      }
    },
    [filterParams, addFilterParams, removeFilterParams]
  );

  const handleChangeFilterLimit = (limit: number) => {
    setLimit(limit);
    resetPaginationSessionStorage();
  };

  const handleChangeFilterSortBy = (sortBy: string) => {
    setQueryParams({
      sort: sortBy,
      page: undefined,
    });

    setSortBy(sortBy);
    resetPaginationSessionStorage();
  };

  const onProductPageChange = (
    page: number,
    options?: {
      loadProductsBehavior?: LoadProductsBehavior;
    },
    collectionSelected?: CurrentCollectionFilterState
  ) => {
    return getFilterData(
      {
        page,
        filterParams: firstFilterParams,
        inCollectionSearchValue: getQueryParamByKey(termKey) as string,
        limit,
      },
      options,
      collectionSelected
    );
  };

  const providerValue: FilterStateValue = {
    filterParams,
    filterData,
    sharedAdditionalElementData,
    currentCollectionFilter: currentCollectionSelected.handle,
    addFilterParams,
    removeFilterParams,
    toggleFilterParams,
    removeFilterParamsOption,
    getFilterData,
    handleChangeFilterLimit,
    handleChangeFilterSortBy,
    addInCollectionSearchParams,
    handleChangeFilterParams,
    currentCollectionSelected,
    setCurrentCollectionSelected,
    filterOptionSelected,
    setFilterOptionSelected,
    onProductPageChange,
    historyCollection,
    setHistoryCollection,
    ajaxLoading,
    setAjaxLoading,
    limit,
    sortBy,
    scopedFilterType,
  };

  useMountEffect(function initFilterData() {
    getFilterData({ filterParams, inCollectionSearchValue: currentCollectionSearchParam, limit });
  });

  return (
    <FilterStateContext.Provider value={providerValue}>{children}</FilterStateContext.Provider>
  );
};

export const FilterStateProvider = registryComponent(
  'FilterStateProvider',
  FilterStateProviderComponent
);

export const useFilterState = (): FilterStateValue => {
  const context = useContext(FilterStateContext);

  if (!context) {
    throw Error('Use useFilterState in FilterStateProvider');
  }

  return context;
};

export const useProductsFilterResult = () => {
  const {
    filterData,
    sharedAdditionalElementData,
    onProductPageChange,
    currentCollectionSelected,
  } = useFilterState();

  const products = useMemo(() => {
    return filterData.data?.products || [];
  }, [filterData]) as ProductItem[];

  const limit = sharedAdditionalElementData.limit;
  const totalProducts = filterData.data?.total_product || 0;
  const page = getFilterPageByDefault();
  const totalPages = Math.ceil(totalProducts / limit);

  return useStateAsExternalStore({
    products,
    totalProducts,
    fetchingData: filterData.loading,
    onProductPageChange,
    currentCollectionSelected,
    pagination: {
      page,
      totalPages,
      limit,
    },
  });
};

export const useProductsFilterResultState = () => {
  const {
    filterData,
    sharedAdditionalElementData,
    onProductPageChange,
    currentCollectionSelected,
  } = useFilterState();

  const products = useMemo(() => {
    return filterData.data?.products || [];
  }, [filterData]) as ProductItem[];

  const limit = sharedAdditionalElementData.limit;
  const totalProducts = filterData.data?.total_product || 0;
  const page = getFilterPageByDefault();
  const totalPages = Math.ceil(totalProducts / limit);

  return {
    products,
    totalProducts,
    fetchingData: filterData.loading,
    onProductPageChange,
    currentCollectionSelected,
    pagination: {
      page,
      totalPages,
      limit,
    },
  };
};

export const useAdditionalElementFilterResult = () => {
  const { sharedAdditionalElementData, handleChangeFilterLimit, handleChangeFilterSortBy } =
    useFilterState();

  const { totalProducts, currentProductCount, limit, totalPages } = sharedAdditionalElementData;
  const page = getFilterPageByDefault();

  const getFromProductIndex = (
    currentProductCount: number,
    loadProductsBehavior?: LoadProductsBehavior
  ): number => {
    if (currentProductCount === 0) return 0;

    if (loadProductsBehavior === 'previous') {
      return (getSessionStorage(PREV_PAGE) - 1) * limit + 1;
    }

    if (loadProductsBehavior === 'more') {
      return (getSessionStorage(PREV_PAGE) - 1) * limit + 1;
    }

    const max = Math.max(limit, currentProductCount);

    return page * limit - max + 1;
  };

  const getToProductIndex = (
    currentProductCount: number,
    totalProduct: number,
    loadProductsBehavior?: LoadProductsBehavior
  ): number => {
    if (page === totalPages) return totalProduct;

    if (loadProductsBehavior === 'previous') {
      if (getSessionStorage(NEXT_PAGE) === totalPages) return totalProduct;

      return getSessionStorage(NEXT_PAGE) * limit;
    }

    if (loadProductsBehavior === 'more') {
      return getSessionStorage(NEXT_PAGE) * limit;
    }

    return Math.min(page * limit, totalProduct);
  };

  const onChangeFilterLimit = useCallback((limit: number) => {
    return handleChangeFilterLimit(limit);
  }, []);

  const onChangeFilterSortBy = useCallback((sortBy: string) => {
    return handleChangeFilterSortBy(sortBy);
  }, []);

  const fromProductIndex = getFromProductIndex(
    currentProductCount,
    sharedAdditionalElementData.loadProductsBehavior
  );

  const toProductIndex = getToProductIndex(
    currentProductCount,
    totalProducts,
    sharedAdditionalElementData.loadProductsBehavior
  );

  return useStateAsExternalStore<SharedAdditionalElementFilterData>({
    ...sharedAdditionalElementData,
    fromProductIndex,
    toProductIndex,
    onChangeFilterLimit,
    onChangeFilterSortBy,
  });
};
